'use client';

import posthog from 'posthog-js';

/**
 * Single owner of browser PostHog initialisation.
 *
 * Both <PostHogProvider> and the App Router error boundaries need an initialised SDK, and the
 * boundaries can render before the provider's auth round-trip finishes — or, in the case of
 * app/global-error.tsx, without the provider ever mounting. Everything therefore goes through
 * `ensurePostHogBrowser()` rather than each call site deciding for itself.
 *
 * Keeping the init flag here also means no app code reads `posthog.__loaded`, which is SDK
 * private API that could change without a type error.
 */
const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;

let initialized = false;

/**
 * Exceptions are billed at roughly 7x an analytics event (PostHog: 100k/month free, then
 * ~$0.00037 each vs ~$0.00005), so what reaches the wire is filtered rather than everything
 * the browser happens to throw.
 *
 * These patterns are noise we can neither see nor fix: cross-origin script errors with no
 * stack, a benign ResizeObserver notification the spec tells you to ignore, and fetches the
 * browser cancelled because the user navigated away.
 */
const EXCEPTION_NOISE = [
  /^Script error\.?$/i,
  /ResizeObserver loop/i,
  /^The (?:user aborted a request|operation was aborted)/i,
];

const EXTENSION_FRAME = /^(?:chrome|moz|safari|webkit)-extension:\/\//;

/**
 * Hard ceiling per page load. A render loop or an interval that throws can otherwise emit
 * thousands of identical exceptions from one session — the failure mode that turns a bug into
 * a bill. Duplicates are dropped first, so hitting this cap means genuinely distinct errors.
 */
const MAX_EXCEPTIONS_PER_PAGE = 25;

const seenExceptions = new Set<string>();
let exceptionsSent = 0;

type ExceptionFrame = { filename?: string };
type ExceptionItem = {
  type?: string;
  value?: string;
  stacktrace?: { frames?: ExceptionFrame[] };
};

/** Returns true when the exception should be dropped before it is billed. */
function isUnbillableNoise(properties: Record<string, unknown>): boolean {
  const list = properties.$exception_list as ExceptionItem[] | undefined;
  const first = list?.[0];
  const value = first?.value ?? '';
  const type = first?.type ?? '';

  if (EXCEPTION_NOISE.some((pattern) => pattern.test(value) || pattern.test(type))) {
    return true;
  }

  // Errors thrown by browser extensions are not our code and not actionable.
  const frames = first?.stacktrace?.frames ?? [];
  if (frames.length > 0 && frames.every((frame) => EXTENSION_FRAME.test(frame.filename ?? ''))) {
    return true;
  }

  if (exceptionsSent >= MAX_EXCEPTIONS_PER_PAGE) {
    return true;
  }

  const key = `${type}:${value}:${frames[0]?.filename ?? ''}`;
  if (seenExceptions.has(key)) {
    return true;
  }

  seenExceptions.add(key);
  exceptionsSent += 1;
  return false;
}

export function ensurePostHogBrowser(): boolean {
  if (typeof window === 'undefined' || !POSTHOG_KEY) {
    return false;
  }

  if (initialized) {
    return true;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      // Feeds PostHog's Error Tracking product: these become `$exception` events with a
      // parsed stack, grouped into issues. Console errors stay off on purpose — every
      // warning-level log would otherwise become a billed event and its own issue group.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      before_send: (event) => {
        if (event?.event === '$exception' && isUnbillableNoise(event.properties ?? {})) {
          return null;
        }
        return event;
      },
    });
    initialized = true;
  } catch (initError) {
    console.error('[posthog] Failed to initialise:', initError);
  }

  return initialized;
}

/**
 * Reports an exception, initialising the SDK first if it isn't up yet. Never throws: this runs
 * inside error boundaries, where a telemetry failure would land on an already-broken tree.
 *
 * Errors carrying a `digest` were thrown on the server: Next has already routed them through
 * instrumentation.ts, where they were captured with the real stack and route. All the client
 * holds is a redacted placeholder message, so re-reporting it would bill a second exception
 * and open a second, useless issue group for the same fault.
 */
export function captureBrowserException(
  error: unknown,
  properties?: Record<string, unknown>,
): void {
  try {
    if (typeof (error as { digest?: unknown })?.digest === 'string') {
      return;
    }

    if (!ensurePostHogBrowser()) {
      return;
    }

    posthog.captureException(error, properties);
  } catch (captureError) {
    console.error('[posthog] Failed to capture exception:', captureError);
  }
}
